import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { autoMobileInvoiceService } from '../../../../services/autoMobileInvoiceService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { CirclePlus, RefreshCw, Eye, FileText, CheckCircle2, Clock } from 'lucide-react';
import '../AutoMobilePage.css';

const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await autoMobileInvoiceService.getAll({ pageNumber: 1, pageSize: 200 });
      const d = (res.data as any)?.data ?? [];
      setItems(Array.isArray(d) ? d : []);
    } catch {
      setError(t('automobile.invoice.failedToLoad'));
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const columns: GridColumn[] = [
    { key: 'invoiceNumber', label: t('automobile.invoice.invoiceNumber'), sortable: true, width: '160px',
      render: (v) => <strong>{v}</strong> },
    { key: 'vehicleRegistrationNumber', label: t('automobile.vehicle.registrationNumber'), width: '140px' },
    { key: 'customerName', label: t('automobile.vehicle.customer'), sortable: true, width: '180px' },
    { key: 'jobType', label: t('automobile.invoice.jobType'), width: '120px',
      render: (v) => <span className={`ims-badge auto-job-${v}`}>{t(`automobile.invoice.jobTypes.${v}`, v)}</span> },
    { key: 'totalAmount', label: t('automobile.invoice.total'), width: '120px',
      render: (v) => <span>{fmt(v)}</span> },
    { key: 'amountPaid', label: t('automobile.invoice.paid'), width: '120px',
      render: (v) => <span>{fmt(v)}</span> },
    { key: 'status', label: t('common.status'), width: '110px',
      render: (v) => <span className={`ims-badge auto-badge-${v}`}>{t(`automobile.invoice.statusLabels.${v}`, v)}</span> },
    { key: 'createdAt', label: t('common.createdAt'), width: '140px',
      render: (v) => <span>{v ? new Date(v).toLocaleDateString() : '—'}</span> },
  ];

  const actions: GridAction[] = [
    { icon: <Eye size={16} />, onClick: (r) => navigate(`/automobile/invoices/${r.id}`),
      className: 'grid-action-btn-view', title: t('common.view') },
  ];

  const paidCount = items.filter((i) => i.status === 'Paid').length;
  const issuedCount = items.filter((i) => i.status === 'Issued').length;

  return (
    <Layout>
      <div className="ims-page auto-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('automobile.invoice.title')}</h1>
            <p className="ims-list-subtitle">{t('automobile.invoice.subtitle')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => load(true)} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/automobile/invoices/create')}>
              <CirclePlus size={16} /> {t('automobile.invoice.newInvoice')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-teal"><FileText size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">{t('automobile.invoice.total')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-blue"><Clock size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{issuedCount}</span>
              <span className="ims-stat-label">{t('automobile.invoice.statusLabels.Issued')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-green"><CheckCircle2 size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{paidCount}</span>
              <span className="ims-stat-label">{t('automobile.invoice.statusLabels.Paid')}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid data={items} columns={columns} actions={actions} loading={loading}
            pageSize={10} searchPlaceholder={t('common.search')}
            emptyMessage={t('automobile.invoice.noInvoicesFound')}
            rowsPerPageOptions={[5, 10, 25, 50]} />
        </div>
      </div>
    </Layout>
  );
};

export default InvoiceListPage;
